import axiosInstance from './axiosConfig';

/**
 * Stock Reorder API client.
 *
 * Every call goes through the Node proxy at /api/stock-reorder, which attaches
 * the caller's identity before forwarding to the Python service. JSON responses
 * arrive wrapped as { success, data } — `unwrap` hides that from callers.
 */

const BASE = '/api/stock-reorder';

const unwrap = (res) => (res?.data && 'data' in res.data ? res.data.data : res.data);

/**
 * Pull the human-readable reason out of a failed request.
 * The proxy normalises FastAPI's `detail` into `message`.
 */
export const errorMessage = (error, fallback = 'Something went wrong') =>
    error?.response?.data?.message || error?.message || fallback;

/**
 * Trigger a browser download from a binary endpoint, honouring the filename
 * the server chose in Content-Disposition.
 */
const download = async (path, body, defaultName) => {
    const res = await axiosInstance.post(`${BASE}${path}`, body, { responseType: 'blob' });

    const disposition = res.headers['content-disposition'] || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1].trim()) : defaultName;

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * A blob error body is still JSON — read it back so the user sees the real
 * reason rather than "[object Blob]".
 */
export const readBlobError = async (error, fallback = 'Export failed') => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed.message || parsed.detail || fallback;
        } catch (e) {
            return fallback;
        }
    }
    return errorMessage(error, fallback);
};

/**
 * Reorder runs are posted as multipart because they may carry uploaded
 * workbooks. When a branch has server-side file paths configured there is no
 * file to send, but the endpoint still expects form fields.
 */
const toFormData = ({ params, businessUnitId, files = {} }) => {
    const form = new FormData();
    form.append('movement_days', params.movementDays);
    form.append('lead_time_days', params.leadTimeDays);
    form.append('safety_days', params.safetyDays);
    form.append('business_unit_id', businessUnitId);

    if (files.stockFile) form.append('file', files.stockFile);
    if (files.supplierFile) form.append('supplier_file', files.supplierFile);
    (files.extraFiles || []).forEach((f) => form.append('extra_files', f));

    return form;
};

const multiBody = (businessUnitIds, params) => ({
    business_unit_ids: businessUnitIds,
    movement_days: Number(params.movementDays),
    lead_time_days: Number(params.leadTimeDays),
    safety_days: Number(params.safetyDays)
});

const stockReorderApi = {
    // --- Identity & health ---
    health: () => axiosInstance.get(`${BASE}/health`).then((r) => r.data),
    me: () => axiosInstance.get(`${BASE}/me`).then(unwrap),
    myScope: () => axiosInstance.get(`${BASE}/scopes/me`).then(unwrap),

    // --- Access administration ---
    listScopes: () => axiosInstance.get(`${BASE}/scopes`).then(unwrap),

    /** Reception's own user list — the source for the "add user" picker. */
    listReceptionUsers: () => axiosInstance.get(`${BASE}/reception-users`).then(unwrap),

    /** Register a reception user for this module. Nobody gets in without this. */
    createScope: ({ userId, displayName, email }) =>
        axiosInstance
            .post(`${BASE}/scopes`, {
                user_id: userId,
                display_name: displayName || '',
                email: email || ''
            })
            .then(unwrap),

    setScope: (userId, { businessUnitIds, allowRestrictedSuppliers, isActive }) =>
        axiosInstance
            .put(`${BASE}/scopes/${encodeURIComponent(userId)}`, {
                ...(businessUnitIds !== undefined ? { business_unit_ids: businessUnitIds } : {}),
                ...(allowRestrictedSuppliers !== undefined
                    ? { allow_restricted_suppliers: allowRestrictedSuppliers }
                    : {}),
                ...(isActive !== undefined ? { is_active: isActive } : {})
            })
            .then(unwrap),
    deleteScope: (userId) =>
        axiosInstance.delete(`${BASE}/scopes/${encodeURIComponent(userId)}`).then(unwrap),

    // --- Company master ---
    listCompanies: () => axiosInstance.get(`${BASE}/companies`).then(unwrap),
    createCompany: (name) => axiosInstance.post(`${BASE}/companies`, { name }).then(unwrap),
    deleteCompany: (id) => axiosInstance.delete(`${BASE}/companies/${id}`).then(unwrap),

    // --- Business units (branches) ---
    listBusinessUnits: () => axiosInstance.get(`${BASE}/business-units`).then(unwrap),
    listAllBusinessUnits: () => axiosInstance.get(`${BASE}/business-units/all`).then(unwrap),
    createBusinessUnit: (body) => axiosInstance.post(`${BASE}/business-units`, body).then(unwrap),
    updateBusinessUnit: (id, body) => axiosInstance.put(`${BASE}/business-units/${id}`, body).then(unwrap),
    deleteBusinessUnit: (id) => axiosInstance.delete(`${BASE}/business-units/${id}`).then(unwrap),

    /**
     * Store a branch's stock or supplier workbook. Replaces whatever was there
     * before — a branch holds one current file of each kind.
     */
    uploadBranchFile: (id, kind, file) => {
        const form = new FormData();
        form.append('kind', kind);
        form.append('file', file);
        return axiosInstance.post(`${BASE}/business-units/${id}/files`, form).then(unwrap);
    },
    deleteBranchFile: (id, kind) =>
        axiosInstance.delete(`${BASE}/business-units/${id}/files/${kind}`).then(unwrap),

    // --- Reorder generation ---
    generate: (opts) =>
        axiosInstance.post(`${BASE}/generate`, toFormData(opts)).then(unwrap),
    generateMulti: (businessUnitIds, params) =>
        axiosInstance.post(`${BASE}/generate-multi`, multiBody(businessUnitIds, params)).then(unwrap),

    // --- Manual overrides ---
    setReorderOverride: ({ businessUnitId, partNumber, needsReorder, reorderQty }) =>
        axiosInstance
            .put(`${BASE}/reorder-overrides`, {
                business_unit_id: businessUnitId,
                part_number: partNumber,
                ...(needsReorder !== undefined ? { needs_reorder: needsReorder } : {}),
                ...(reorderQty !== undefined ? { reorder_qty: reorderQty } : {})
            })
            .then(unwrap),
    clearReorderOverride: (partNumber, businessUnitId) =>
        axiosInstance
            .delete(`${BASE}/reorder-overrides/${encodeURIComponent(partNumber)}`, {
                params: { business_unit_id: businessUnitId }
            })
            .then(unwrap),

    // --- Supplier master (admin) ---
    listSupplierOverrides: (businessUnitId) =>
        axiosInstance
            .get(`${BASE}/suppliers/overrides`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    deleteSupplierOverride: (partNumber, businessUnitId) =>
        axiosInstance
            .delete(`${BASE}/suppliers/overrides/${encodeURIComponent(partNumber)}`, {
                params: { business_unit_id: businessUnitId }
            })
            .then(unwrap),
    bulkUploadSuppliers: (businessUnitId, file) => {
        const form = new FormData();
        form.append('business_unit_id', businessUnitId);
        form.append('file', file);
        return axiosInstance.post(`${BASE}/suppliers/bulk-upload`, form).then(unwrap);
    },

    // --- Supplier map: part number → supplier (admin) ---
    supplierMapStats: (businessUnitId) =>
        axiosInstance
            .get(`${BASE}/supplier-map/stats`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    listSupplierMap: (businessUnitId, { search = '', onlyConflicts = false, limit = 100, offset = 0 } = {}) =>
        axiosInstance
            .get(`${BASE}/supplier-map`, {
                params: {
                    business_unit_id: businessUnitId,
                    search,
                    only_conflicts: onlyConflicts,
                    limit,
                    offset
                }
            })
            .then(unwrap),
    /**
     * `mode` is 'add_new' (existing rows untouched) or 'replace_all'.
     * Omitting the file imports the branch's stored supplier workbook.
     */
    // --- Stock availability (uploaded ERP Stock Report) ---
    uploadStockReport: (file, businessUnitId = '') => {
        const form = new FormData();
        form.append('file', file);
        if (businessUnitId) form.append('business_unit_id', businessUnitId);
        return axiosInstance.post(`${BASE}/stock/uploads`, form).then(unwrap);
    },
    stockPreviewForBranch: (uploadId, businessUnitId) =>
        axiosInstance
            .get(`${BASE}/stock/uploads/${uploadId}/preview`, {
                params: { business_unit_id: businessUnitId }
            })
            .then(unwrap),
    confirmStockUpload: (uploadId, businessUnitId) =>
        axiosInstance
            .post(`${BASE}/stock/uploads/${uploadId}/confirm`, { business_unit_id: businessUnitId })
            .then(unwrap),
    listStockUploads: (businessUnitId) =>
        axiosInstance
            .get(`${BASE}/stock/uploads`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    getStock: (businessUnitId, uploadId = '') =>
        axiosInstance
            .get(`${BASE}/stock/current`, {
                params: { business_unit_id: businessUnitId, ...(uploadId ? { upload_id: uploadId } : {}) }
            })
            .then(unwrap),
    deleteStockUpload: (uploadId) =>
        axiosInstance.delete(`${BASE}/stock/uploads/${uploadId}`).then(unwrap),

    importSupplierMap: (businessUnitId, { mode = 'add_new', file = null } = {}) => {
        const form = new FormData();
        form.append('business_unit_id', businessUnitId);
        form.append('mode', mode);
        if (file) form.append('file', file);
        return axiosInstance.post(`${BASE}/supplier-map/import`, form).then(unwrap);
    },
    addSupplierMapRow: ({ businessUnitId, partNumber, supplierName }) =>
        axiosInstance
            .post(`${BASE}/supplier-map`, {
                business_unit_id: businessUnitId,
                part_number: partNumber,
                supplier_name: supplierName
            })
            .then(unwrap),
    updateSupplierMapRow: (rowId, supplierName) =>
        axiosInstance
            .put(`${BASE}/supplier-map/${encodeURIComponent(rowId)}`, { supplier_name: supplierName })
            .then(unwrap),
    deleteSupplierMapRow: (rowId) =>
        axiosInstance.delete(`${BASE}/supplier-map/${encodeURIComponent(rowId)}`).then(unwrap),

    // --- Supplier flags: international / restricted (admin) ---
    listSupplierFlags: () => axiosInstance.get(`${BASE}/supplier-flags`).then(unwrap),
    listKnownSupplierNames: () => axiosInstance.get(`${BASE}/supplier-names`).then(unwrap),
    addSupplierFlag: ({ name, isInternational, isRestricted }) =>
        axiosInstance
            .post(`${BASE}/supplier-flags`, {
                name,
                is_international: isInternational,
                is_restricted: isRestricted
            })
            .then(unwrap),
    updateSupplierFlag: (id, { isInternational, isRestricted }) =>
        axiosInstance
            .put(`${BASE}/supplier-flags/${id}`, {
                ...(isInternational !== undefined ? { is_international: isInternational } : {}),
                ...(isRestricted !== undefined ? { is_restricted: isRestricted } : {})
            })
            .then(unwrap),
    deleteSupplierFlag: (id) =>
        axiosInstance.delete(`${BASE}/supplier-flags/${id}`).then(unwrap),

    /**
     * Draft — the last generated sheet, stashed server-side automatically.
     * There is no "save" call: /generate writes it.
     */
    getDraft: () => axiosInstance.get(`${BASE}/drafts`).then(unwrap),
    openDraft: (id) => axiosInstance.get(`${BASE}/drafts/${id}`).then(unwrap),
    keepDraft: (id, label) =>
        axiosInstance.post(`${BASE}/drafts/${id}/keep`, { label: label || '' }).then(unwrap),
    discardDraft: (id) => axiosInstance.delete(`${BASE}/drafts/${id}`).then(unwrap),

    // --- Saved history (private per user) ---
    listHistory: (businessUnitId) =>
        axiosInstance
            .get(`${BASE}/history`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    getHistory: (id, businessUnitId) =>
        axiosInstance
            .get(`${BASE}/history/${id}`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    deleteHistory: (id, businessUnitId) =>
        axiosInstance
            .delete(`${BASE}/history/${id}`, { params: { business_unit_id: businessUnitId } })
            .then(unwrap),
    saveHistory: (opts) => {
        const form = toFormData(opts);
        if (opts.label) form.append('label', opts.label);
        return axiosInstance.post(`${BASE}/history`, form).then(unwrap);
    },

    // --- Exports (binary downloads) ---
    exportExcel: (opts, supplier) => {
        const form = toFormData(opts);
        if (supplier) form.append('supplier', supplier);
        return download('/export', form, 'Reorder_Sheet.xlsx');
    },
    exportPdf: (opts, supplier) => {
        const form = toFormData(opts);
        if (supplier) form.append('supplier', supplier);
        return download('/export-pdf', form, 'Reorder_Sheet.pdf');
    },
    exportSupplierOrder: (opts, supplier) => {
        const form = toFormData(opts);
        form.append('supplier', supplier);
        return download('/export-supplier-order', form, 'SupplierOrder.csv');
    },
    exportMultiExcel: (businessUnitIds, params) =>
        download('/export-multi', multiBody(businessUnitIds, params), 'Reorder_Sheet_Combined.xlsx'),
    exportMultiPdf: (businessUnitIds, params) =>
        download('/export-multi-pdf', multiBody(businessUnitIds, params), 'Reorder_Sheet_Combined.pdf'),

    /**
     * Export a sheet that is already stored server-side — a restored draft or a
     * saved sheet.
     *
     * The exports above re-run the calculation, so they need the request that
     * produced the sheet, uploaded workbooks included. A stored sheet has the
     * results but not those inputs, which is why its export buttons used to do
     * nothing at all. This builds the file from the stored items instead.
     */
    exportSnapshot: (sheetId, format, supplier) => {
        const defaults = {
            csv: 'SupplierOrder.csv',
            pdf: 'Reorder_Sheet.pdf',
            xlsx: 'Reorder_Sheet.xlsx'
        };
        return download(
            '/export-snapshot',
            { sheet_id: sheetId, format, ...(supplier ? { supplier } : {}) },
            defaults[format] || defaults.xlsx
        );
    }
};

export default stockReorderApi;
